import { Badge } from '@workspace/ui/components/badge'
import { Button } from '@workspace/ui/components/button'
import type { TableColumn } from '@workspace/ui/components/data-table'
import { CellPrimary, CellText, DataTable } from '@workspace/ui/components/data-table'
import { StatCell } from '@workspace/ui/components/stat-cell'
import { useState } from 'react'
import {
  useAdminRescanStartupFactSourceMutation,
  useAdminStartupFactSourcesQuery,
  useAdminStartupFactsQuery,
} from '../hooks/use-admin-api'

const PAGE_SIZE = 25
const DIMENSIONS = [
  'team',
  'market',
  'product',
  'traction',
  'moat',
  'gtm',
  'unit_economics',
  'competition',
  'regulatory',
  'round',
] as const
const SOURCE_KINDS = ['DECK', 'DOCUMENT', 'EMAIL_UPDATE', 'FOUNDER_SPEECH'] as const

const NOTE_LABEL: Record<string, string> = {
  no_text: 'No text extracted',
  not_published: 'Session not published',
  version_gone: 'Source removed',
  session_gone: 'Source removed',
  document_gone: 'Source removed',
}

function formatDate(value: Date | string) {
  return new Date(value).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  })
}

function scanLabel(status: string, parsingStatus: string | null) {
  if (status === 'PENDING' && parsingStatus && parsingStatus !== 'COMPLETED') {
    return `Waiting for text (${parsingStatus.toLowerCase()})`
  }
  return status.charAt(0) + status.slice(1).toLowerCase()
}

type Source = NonNullable<
  ReturnType<typeof useAdminStartupFactSourcesQuery>['data']
>['data'][number]
type Fact = NonNullable<
  ReturnType<typeof useAdminStartupFactsQuery>['data']
>['data']['items'][number]

const KINDS = [
  'paying_customer',
  'revenue_level',
  'live_pilot_or_deployment',
  'product_shipped_or_demo',
  'validation_or_test_result',
  'repeat_usage_or_retention',
  'team_change',
  'round_or_terms_change',
] as const

export function StartupFactsTab({ startupId }: { startupId: string }) {
  const [current, setCurrent] = useState(true)
  const [sourceKind, setSourceKind] = useState<(typeof SOURCE_KINDS)[number] | ''>('')
  const [dimension, setDimension] = useState('')
  const [kind, setKind] = useState('')
  const [search, setSearch] = useState('')
  const [page, setPage] = useState(1)

  const sourcesQuery = useAdminStartupFactSourcesQuery(startupId)
  const rescan = useAdminRescanStartupFactSourceMutation(startupId)
  const factsQuery = useAdminStartupFactsQuery(startupId, {
    current,
    sourceKind: sourceKind || undefined,
    dimension: dimension || undefined,
    kind: kind || undefined,
    q: search.trim() || undefined,
    page,
    limit: PAGE_SIZE,
  })

  const sources = sourcesQuery.data?.data ?? []
  const facts = factsQuery.data?.data
  const pending = sources.filter((s) => s.factScanStatus === 'PENDING').length
  const failed = sources.filter((s) => s.factScanStatus === 'FAILED').length
  const totalPages = facts ? Math.max(1, Math.ceil(facts.total / facts.limit)) : 1

  const sourceColumns: TableColumn<Source>[] = [
    {
      header: 'Source',
      render: (s) => (
        <div className="space-y-0.5">
          <CellPrimary>{s.sourceName}</CellPrimary>
          <CellText className="block text-xs">
            {s.kind.replace('_', ' ').toLowerCase()}
            {s.isSuperseded ? ' · replaced' : ''}
          </CellText>
        </div>
      ),
    },
    {
      header: 'Appeared',
      width: '120px',
      render: (s) => <CellText>{formatDate(s.occurredAt)}</CellText>,
    },
    {
      header: 'Scan',
      width: '200px',
      render: (s) => (
        <Badge variant={s.factScanStatus === 'FAILED' ? 'destructive' : 'secondary'}>
          {scanLabel(s.factScanStatus, s.parsingStatus)}
        </Badge>
      ),
    },
    { header: 'Facts', width: '70px', render: (s) => <CellText>{s.factCount ?? '-'}</CellText> },
    {
      header: 'Interviewer lines excluded',
      width: '150px',
      render: (s) => <CellText>{s.interviewerLinesExcluded ?? '-'}</CellText>,
    },
    {
      header: 'Note',
      render: (s) => (
        <CellText>{s.factScanNote ? (NOTE_LABEL[s.factScanNote] ?? s.factScanNote) : '-'}</CellText>
      ),
    },
    {
      header: '',
      width: '110px',
      render: (s) => (
        <Button
          size="sm"
          variant="outline"
          disabled={rescan.isPending || s.factScanStatus === 'PENDING'}
          onClick={() => rescan.mutate({ path: { id: startupId, changeId: s.changeId } })}
        >
          Re-scan
        </Button>
      ),
    },
  ]

  const factColumns: TableColumn<Fact>[] = [
    { header: 'Statement', render: (f) => <span>{f.text}</span> },
    {
      header: 'Source',
      width: '200px',
      render: (f) => (
        <div className="space-y-0.5">
          <CellText className="block">{f.sourceLabel}</CellText>
          {f.speaker && <CellText className="block text-xs">{f.speaker}</CellText>}
        </div>
      ),
    },
    {
      header: 'Date',
      width: '110px',
      render: (f) => <CellText>{formatDate(f.sourceDate)}</CellText>,
    },
    {
      header: 'Topics',
      width: '170px',
      render: (f) => <CellText>{f.dimensions.length > 0 ? f.dimensions.join(', ') : '-'}</CellText>,
    },
    {
      header: 'Could satisfy',
      width: '170px',
      render: (f) => (
        <CellText>
          {f.kinds.length > 0 ? f.kinds.map((k) => k.replaceAll('_', ' ')).join(', ') : '-'}
          {f.isNumeric ? '' : ' (no number)'}
        </CellText>
      ),
    },
    {
      header: 'Claim id',
      width: '120px',
      render: (f) => <CellText className="font-mono text-xs">{f.claimId}</CellText>,
    },
    {
      header: 'State',
      width: '90px',
      render: (f) => (
        <CellText>{f.retired ? 'Retired' : f.isCurrent ? 'Current' : 'Historic'}</CellText>
      ),
    },
  ]

  return (
    <div className="space-y-8">
      <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
        <StatCell size="sm" value={facts?.currentTotal ?? '-'} label="Current facts" />
        <StatCell size="sm" value={sources.length} label="Sources scanned or queued" />
        <StatCell size="sm" value={pending} label="Waiting to be scanned" />
        <StatCell size="sm" value={failed} label="Failed scans" accent={failed > 0} />
      </div>

      <section className="space-y-3">
        <h3 className="text-sm font-semibold">Sources</h3>
        <p className="text-xs text-muted-foreground">
          Every document, deck and published session the fact scan reads. A source waits here until
          the engine has extracted its text.
        </p>
        <DataTable
          columns={sourceColumns}
          data={sources}
          isLoading={sourcesQuery.isLoading}
          emptyMessage="Nothing has been queued for this startup yet."
          keyExtractor={(s) => s.changeId}
          minWidth="900px"
        />
      </section>

      <section className="space-y-3">
        <h3 className="text-sm font-semibold">Facts</h3>
        <div className="flex flex-wrap items-center gap-3">
          <select
            aria-label="Current or historic"
            value={current ? 'current' : 'historic'}
            onChange={(e) => {
              setCurrent(e.target.value === 'current')
              setPage(1)
            }}
            className="rounded-md border border-border bg-background px-3 py-2 text-sm"
          >
            <option value="current">Current</option>
            <option value="historic">Historic (replaced sources)</option>
          </select>
          <select
            aria-label="Source kind"
            value={sourceKind}
            onChange={(e) => {
              setSourceKind(e.target.value as (typeof SOURCE_KINDS)[number] | '')
              setPage(1)
            }}
            className="rounded-md border border-border bg-background px-3 py-2 text-sm"
          >
            <option value="">All sources</option>
            {SOURCE_KINDS.map((kind) => (
              <option key={kind} value={kind}>
                {kind.replace('_', ' ').toLowerCase()}
              </option>
            ))}
          </select>
          <select
            aria-label="Topic"
            value={dimension}
            onChange={(e) => {
              setDimension(e.target.value)
              setPage(1)
            }}
            className="rounded-md border border-border bg-background px-3 py-2 text-sm"
          >
            <option value="">All topics</option>
            {DIMENSIONS.map((name) => (
              <option key={name} value={name}>
                {name} ({facts?.countsByDimension[name] ?? 0})
              </option>
            ))}
          </select>
          <select
            aria-label="Could satisfy"
            value={kind}
            onChange={(e) => {
              setKind(e.target.value)
              setPage(1)
            }}
            className="rounded-md border border-border bg-background px-3 py-2 text-sm"
          >
            <option value="">All kinds</option>
            {KINDS.map((name) => (
              <option key={name} value={name}>
                {name.replaceAll('_', ' ')}
              </option>
            ))}
          </select>
          <input
            type="search"
            aria-label="Search the statements"
            placeholder="Search statements"
            value={search}
            onChange={(e) => {
              setSearch(e.target.value)
              setPage(1)
            }}
            className="w-64 rounded-md border border-border bg-background px-3 py-2 text-sm"
          />
        </div>
        <DataTable
          columns={factColumns}
          data={facts?.items ?? []}
          isLoading={factsQuery.isLoading}
          emptyMessage="No facts match."
          keyExtractor={(f) => f.id}
          minWidth="1000px"
        />
        <div className="flex items-center justify-between text-sm text-muted-foreground">
          <span>{facts ? `${facts.total} facts` : ''}</span>
          <div className="flex items-center gap-2">
            <Button
              size="sm"
              variant="outline"
              disabled={page <= 1}
              onClick={() => setPage(page - 1)}
            >
              Previous
            </Button>
            <span>
              Page {page} of {totalPages}
            </span>
            <Button
              size="sm"
              variant="outline"
              disabled={page >= totalPages}
              onClick={() => setPage(page + 1)}
            >
              Next
            </Button>
          </div>
        </div>
      </section>
    </div>
  )
}
